import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getApiBaseUrl, publicMediaSrc } from "../config/api";
import { adminJson, getAdminSession } from "../utils/adminSession";
import { compactIntlPhone, formatIntlPhone } from "../utils/formatIntlPhone";

const EMPTY = {
  companyName: "",
  companyRegNo: "",
  siteName: "",
  emailAddress: "",
  whatsappNumber: "",
  siteLogo: "",
  address: "",
  instagram: "",
  facebook: "",
};

const inputClass =
  "w-full rounded-md border border-neutral-200 bg-white px-3 py-2 text-sm text-at-dark outline-none focus:border-at-gold";
const WHATSAPP_RE = /^\+[1-9]\d{7,14}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX_LOGO_BYTES = 2 * 1024 * 1024;

function fieldsFromContact(doc = EMPTY) {
  return {
    companyName: doc.companyName || "",
    companyRegNo: doc.companyRegNo || "",
    siteName: doc.siteName || "",
    emailAddress: doc.emailAddress || "",
    whatsappNumber: formatIntlPhone(doc.whatsappNumber || ""),
    siteLogo: doc.siteLogo || "",
    address: doc.address || "",
    instagram: doc.instagram || "",
    facebook: doc.facebook || "",
  };
}

function fileNameFromPath(path) {
  const cleaned = String(path || "").split("?")[0];
  const part = cleaned.split("/").filter(Boolean).pop() || "";
  return part || "No file selected";
}

function formatUpdatedAt(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function AdminContactPage() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const session = getAdminSession();
  const [contactId, setContactId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saved, setSaved] = useState(EMPTY);
  const [updatedAt, setUpdatedAt] = useState("");
  const [previewTab, setPreviewTab] = useState("header");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(saved), [form, saved]);
  const logoSrc = publicMediaSrc(form.siteLogo);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const list = await fetch(`${getApiBaseUrl()}/contacts`, { headers: { Accept: "application/json" } }).then((res) => {
          if (!res.ok) throw new Error("Unable to load contact");
          return res.json();
        });
        if (cancelled) return;
        const current = Array.isArray(list) && list[0] ? list[0] : null;
        if (!current) {
          setContactId(null);
          setForm(EMPTY);
          setSaved(EMPTY);
          setUpdatedAt("");
          return;
        }
        const next = fieldsFromContact(current);
        setContactId(current._id);
        setForm(next);
        setSaved(next);
        setUpdatedAt(current.updatedAt || "");
      } catch (err) {
        if (!cancelled) setError(err?.message || "Unable to load contact");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setNotice("");
  }

  function onDiscard() {
    setForm(saved);
    setError("");
    setNotice("");
  }

  function validate() {
    if (!form.companyName.trim()) return "Company name is required";
    if (!form.companyRegNo.trim()) return "Company registration number is required";
    if (!form.siteName.trim()) return "Site name is required";
    if (!EMAIL_RE.test(form.emailAddress.trim())) return "Enter a valid email address";
    const whatsappNumber = compactIntlPhone(form.whatsappNumber);
    if (whatsappNumber && !WHATSAPP_RE.test(whatsappNumber)) {
      return "WhatsApp number must start with + and digits";
    }
    if (!form.siteLogo.trim()) return "Website logo is required";
    return "";
  }

  async function onSave() {
    const invalid = validate();
    if (invalid) {
      setError(invalid);
      return;
    }
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const payload = {
        companyName: form.companyName.trim(),
        companyRegNo: form.companyRegNo.trim(),
        siteName: form.siteName.trim(),
        emailAddress: form.emailAddress.trim(),
        whatsappNumber: compactIntlPhone(form.whatsappNumber),
        siteLogo: form.siteLogo.trim(),
        address: form.address.trim(),
        instagram: form.instagram.trim(),
        facebook: form.facebook.trim(),
      };
      const savedDoc = contactId
        ? await adminJson(`/contacts/${contactId}`, { method: "PUT", body: payload })
        : await adminJson("/contacts", { method: "POST", body: payload });
      const next = fieldsFromContact(savedDoc);
      setContactId(savedDoc._id);
      setForm(next);
      setSaved(next);
      setUpdatedAt(savedDoc.updatedAt || "");
      setNotice("Changes saved.");
    } catch (err) {
      if (err?.status === 401) {
        navigate("/login", { replace: true });
        return;
      }
      setError(err?.message || "Unable to save contact");
    } finally {
      setSaving(false);
    }
  }

  async function onReplaceLogo(file) {
    if (!file) return;
    if (!["image/png", "image/webp"].includes(file.type)) {
      setError("Logo must be a PNG or WebP file");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setError("Logo must be 2 MB or smaller");
      return;
    }
    setUploading(true);
    setError("");
    try {
      const data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = () => reject(new Error("Unable to read file"));
        reader.readAsDataURL(file);
      });
      const uploaded = await adminJson("/media", {
        method: "POST",
        body: {
          purpose: "contact",
          filename: file.name,
          contentType: file.type,
          data,
        },
      });
      setField("siteLogo", uploaded.path);
    } catch (err) {
      if (err?.status === 401) {
        navigate("/login", { replace: true });
        return;
      }
      setError(err?.message || "Unable to upload logo");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  if (loading) {
    return <p className="text-sm text-neutral-500">Loading contact…</p>;
  }

  return (
    <div className="-mt-2">
      <p className="text-xs text-neutral-400">Dashboard / Contact Information</p>
      <div className="mt-2 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-at-dark">Contact Information</h1>
          <p className="mt-1 text-sm text-neutral-500">Manage the company details displayed across your website.</p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={!dirty || saving}
            onClick={onDiscard}
            className="rounded-md border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-at-dark disabled:opacity-40"
          >
            Discard Changes
          </button>
          <button
            type="button"
            disabled={saving || uploading}
            onClick={onSave}
            className="rounded-md bg-at-gold px-4 py-2 text-sm font-semibold text-at-dark hover:bg-at-gold-hover disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      <div className="mt-5 rounded-md border border-amber-200 bg-[#f7eedf] px-4 py-3 text-sm text-neutral-700">
        Changes saved here will automatically update the website header, footer, contact page and enquiry links.
      </div>

      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      {notice && <p className="mt-4 text-sm text-emerald-700">{notice}</p>}

      <div className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-6">
          <section className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
            <h2 className="text-base font-semibold text-at-dark">Company Details</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Field label="Company Name *" htmlFor="companyName">
                <input
                  id="companyName"
                  value={form.companyName}
                  onChange={(e) => setField("companyName", e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="Company Reg No. *" htmlFor="companyRegNo">
                <input
                  id="companyRegNo"
                  value={form.companyRegNo}
                  onChange={(e) => setField("companyRegNo", e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="Site Name *" htmlFor="siteName">
                <input
                  id="siteName"
                  value={form.siteName}
                  onChange={(e) => setField("siteName", e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="Email Address *" htmlFor="emailAddress" hint="Used for public enquiries and form notifications.">
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">@</span>
                  <input
                    id="emailAddress"
                    type="email"
                    value={form.emailAddress}
                    onChange={(e) => setField("emailAddress", e.target.value)}
                    className={`${inputClass} pl-8`}
                  />
                </div>
              </Field>
              <Field label="WhatsApp Number" htmlFor="whatsappNumber" hint="Optional. Include country code, beginning with +.">
                <input
                  id="whatsappNumber"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={form.whatsappNumber}
                  onChange={(e) => setField("whatsappNumber", formatIntlPhone(e.target.value))}
                  placeholder="60 12 345 6789"
                  className={inputClass}
                />
              </Field>
              <Field label="Instagram" htmlFor="instagram">
                <input
                  id="instagram"
                  value={form.instagram}
                  onChange={(e) => setField("instagram", e.target.value)}
                  placeholder="https://instagram.com/..."
                  className={inputClass}
                />
              </Field>
              <Field label="Facebook" htmlFor="facebook">
                <input
                  id="facebook"
                  value={form.facebook}
                  onChange={(e) => setField("facebook", e.target.value)}
                  placeholder="https://facebook.com/..."
                  className={inputClass}
                />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Address" htmlFor="address">
                  <textarea
                    id="address"
                    rows={3}
                    value={form.address}
                    onChange={(e) => setField("address", e.target.value)}
                    placeholder="Enter your business address"
                    className={`${inputClass} resize-y`}
                  />
                </Field>
              </div>
            </div>
            {updatedAt && (
              <p className="mt-4 text-xs text-neutral-400">
                Last updated {formatUpdatedAt(updatedAt)}
                {session?.email ? ` by ${session.email}` : ""}
              </p>
            )}
          </section>

          <section className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
            <h2 className="text-base font-semibold text-at-dark">Website Logo *</h2>
            <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-lg border border-neutral-200 bg-neutral-50">
                {logoSrc ? (
                  <img src={logoSrc} alt="" className="h-full w-full object-contain p-2" />
                ) : (
                  <span className="text-xs text-neutral-400">No logo</span>
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-at-dark">{fileNameFromPath(form.siteLogo)}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/webp"
                    className="hidden"
                    onChange={(e) => onReplaceLogo(e.target.files?.[0])}
                  />
                  <button
                    type="button"
                    disabled={uploading}
                    onClick={() => fileRef.current?.click()}
                    className="rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-sm text-at-dark disabled:opacity-60"
                  >
                    {uploading ? "Uploading…" : "Replace Logo"}
                  </button>
                  <button
                    type="button"
                    disabled={!form.siteLogo}
                    onClick={() => setField("siteLogo", "")}
                    className="rounded-md border border-red-200 px-3 py-1.5 text-sm text-red-600 disabled:opacity-40"
                  >
                    Remove
                  </button>
                </div>
                <p className="mt-2 text-xs text-neutral-400">Recommended: transparent PNG or WebP, maximum 2 MB.</p>
              </div>
            </div>
          </section>
        </div>

        <div className="space-y-6">
          <section className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-base font-semibold text-at-dark">Website Preview</h2>
              <Link to="/" className="text-xs font-medium text-at-gold hover:text-at-gold-hover">
                Open Website Preview ↗
              </Link>
            </div>
            <div className="mt-3 flex gap-1 rounded-md bg-neutral-100 p-1 text-xs font-medium">
              {["header", "footer", "contact"].map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setPreviewTab(tab)}
                  className={`flex-1 rounded px-2 py-1.5 capitalize ${
                    previewTab === tab ? "bg-white text-at-dark shadow-sm" : "text-neutral-500"
                  }`}
                >
                  {tab === "contact" ? "Contact Page" : tab}
                </button>
              ))}
            </div>
            <div className="mt-4 overflow-hidden rounded-lg border border-neutral-200">
              {previewTab === "header" && <HeaderPreview form={form} logoSrc={logoSrc} />}
              {previewTab === "footer" && <FooterPreview form={form} logoSrc={logoSrc} />}
              {previewTab === "contact" && <ContactPreview form={form} logoSrc={logoSrc} />}
            </div>
          </section>

          <section className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
            <h2 className="text-base font-semibold text-at-dark">Contact Channels</h2>
            <ChannelRow label="Email Enquiries" active={Boolean(form.emailAddress.trim())} />
            <ChannelRow label="WhatsApp Enquiries" active={Boolean(form.whatsappNumber.trim())} />
            <ChannelRow label="Instagram" active={Boolean(form.instagram.trim())} />
            <ChannelRow label="Facebook" active={Boolean(form.facebook.trim())} />
            <p className="mt-3 text-xs text-neutral-400">These links are generated from the information entered on this page.</p>
          </section>

          <section className="rounded-xl border border-neutral-200 bg-white p-5 shadow-sm">
            <h2 className="text-base font-semibold text-at-dark">Before you save</h2>
            <ul className="mt-3 space-y-2 text-sm text-neutral-600">
              <li>Verify email spelling (e.g. name@company.com).</li>
              <li>Include WhatsApp country code (e.g. +60…).</li>
              <li>Confirm the public address is correct and up to date.</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}

function Field({ label, htmlFor, hint, children }) {
  return (
    <label className="block text-sm" htmlFor={htmlFor}>
      <span className="font-medium text-at-dark">{label}</span>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="mt-1 text-xs text-neutral-400">{hint}</p>}
    </label>
  );
}

function ChannelRow({ label, active }) {
  return (
    <div className="mt-3 flex items-center justify-between rounded-md border border-neutral-100 px-3 py-2.5 text-sm">
      <span className="text-at-dark">{label}</span>
      <span className={`flex items-center gap-1.5 text-xs ${active ? "text-emerald-600" : "text-neutral-400"}`}>
        <span className={`h-2 w-2 rounded-full ${active ? "bg-emerald-500" : "bg-neutral-300"}`} />
        {active ? "Active" : "Inactive"}
      </span>
    </div>
  );
}

function HeaderPreview({ form, logoSrc }) {
  return (
    <div className="bg-at-dark px-3 py-3 text-white">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {logoSrc ? <img src={logoSrc} alt="" className="h-8 w-8 object-contain invert" /> : <span className="h-8 w-8 rounded bg-white/10" />}
          <span className="truncate text-[10px] font-semibold uppercase tracking-wider">
            {form.siteName || form.companyName || "Site name"}
          </span>
        </div>
        <span className="rounded bg-at-gold px-2 py-1 text-[10px] font-semibold text-at-dark">Get a Quote</span>
      </div>
    </div>
  );
}

function FooterPreview({ form, logoSrc }) {
  return (
    <div className="bg-at-darker px-3 py-4 text-[10px] text-white/70">
      <div className="flex items-start gap-2">
        {logoSrc ? <img src={logoSrc} alt="" className="h-8 w-8 object-contain invert" /> : <span className="h-8 w-8 rounded bg-white/10" />}
        <div>
          <p className="font-semibold uppercase tracking-wider text-white">{form.companyName || "Company name"}</p>
          {form.companyRegNo && <p className="mt-0.5 text-white/45">{form.companyRegNo}</p>}
        </div>
      </div>
      <div className="mt-3 space-y-1">
        {form.emailAddress && <p>{form.emailAddress}</p>}
        {form.whatsappNumber && <p>{form.whatsappNumber}</p>}
        {form.address && <p>{form.address}</p>}
      </div>
    </div>
  );
}

function ContactPreview({ form, logoSrc }) {
  return (
    <div className="bg-at-paper px-3 py-4 text-xs text-at-dark">
      {logoSrc && <img src={logoSrc} alt="" className="mb-3 h-10 w-10 object-contain" />}
      <p className="font-semibold">{form.companyName || "Company name"}</p>
      <p className="mt-1 text-neutral-500">{form.emailAddress || "Email"}</p>
      <p className="text-neutral-500">{form.whatsappNumber || "WhatsApp"}</p>
      <p className="mt-2 text-neutral-500">{form.address || "Address"}</p>
    </div>
  );
}

export default AdminContactPage;
